import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice, createSelector } from '@reduxjs/toolkit';
import type { RootState } from 'store/store';
import {
  isQuiz,
  type LaunchedQuizPayload,
  type QuizQuestion,
  type QuizzesState,
} from 'features/quizzes/types';
import {
  quizzesContentCleared,
  signalingQuizCreated,
  signalingQuizDeleted,
  signalingQuizEdited,
  signalingQuizEnded,
  signalingQuizFinished,
  signalingQuizLaunched,
  signalingQuizzesReceived,
  signalingQuizTemplateCreated,
  signalingQuizTemplateDeleted,
  signalingQuizTemplateEdited,
  signalingQuizTemplatesReceived,
} from 'features/quizzes/actions';
import { normalizeQuizzes } from 'features/quizzes/utils/normalizeQuizzes';
import { requestDetailedQuizResults } from 'features/quizzes/thunks/requestDetailedQuizResults';
import { requestQuizResults } from 'features/quizzes/thunks/requestQuizResults';
import { resetQuiz } from 'features/quizzes/utils/resetQuiz';
import { submitQuizQuestion } from 'features/quizzes/thunks/submitQuizQuestion';
import { clearQuizResults } from 'features/quizzes/utils/clearQuizResults';
import { resetActiveQuiz } from 'features/quizzes/utils/resetActiveQuiz';

export const initialState: QuizzesState = {
  exportProcessing: false,
  activeQuizId: null,
  activeQuizSessionId: null,
  entities: {},
  ids: [],
  activeQuiz: {
    localSelections: {},
    launchedAt: null,
    currentQuestionIndex: 0,
  },
  results: {},
  detailedResults: {},
  templates: {
    entities: {},
    ids: [],
  },
};

export const quizzesSlice = createSlice({
  name: 'quizzes',
  initialState,
  reducers: {
    exportQuizResultsRequested: (state) => {
      state.exportProcessing = true;
    },
    exportQuizResultsFulfilled: (state) => {
      state.exportProcessing = false;
    },
    exportQuizResultsRejected: (state) => {
      state.exportProcessing = false;
    },
    localSelectionSaved: (
      state,
      action: PayloadAction<{ questionId: string; selectedChoices: number[] }>
    ) => {
      const { questionId, selectedChoices } = action.payload;
      state.activeQuiz.localSelections[questionId] = selectedChoices;
    },
    quizLaunchedAtSet: (state, action: PayloadAction<number>) => {
      state.activeQuiz.launchedAt = action.payload;
    },
    quizResultsCleared: (state) => {
      clearQuizResults(state);
    },
    quizQuestionNavigatedNext: (state) => {
      state.activeQuiz.currentQuestionIndex += 1;
    },
    quizQuestionNavigatedPrevious: (state) => {
      state.activeQuiz.currentQuestionIndex = Math.max(
        0,
        state.activeQuiz.currentQuestionIndex - 1
      );
    },
    quizQuestionIndexSet: (state, action: PayloadAction<number>) => {
      state.activeQuiz.currentQuestionIndex = Math.max(0, action.payload);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(quizzesContentCleared, (state, action) => {
        const { canManagePolls } = action.payload;

        state.activeQuizId = null;
        state.activeQuizSessionId = null;
        resetActiveQuiz(state);
        clearQuizResults(state);

        if (canManagePolls) {
          for (const id of state.ids) {
            resetQuiz(state.entities[id]);
          }
        } else {
          state.entities = {};
          state.ids = [];
        }
      })
      .addCase(signalingQuizCreated, (state, action) => {
        const quiz = action.payload;

        state.entities[quiz.id] = quiz;
        state.ids.push(quiz.id);
      })
      .addCase(signalingQuizzesReceived, (state, action) => {
        const { ids, entities, activeQuizId, activeQuizSessionId } = normalizeQuizzes(
          action.payload.quizzes
        );

        const sessionChanged =
          state.activeQuizSessionId && state.activeQuizSessionId !== activeQuizSessionId;

        if (sessionChanged && activeQuizId) {
          delete state.activeQuiz.localSelections[activeQuizId];
          delete state.results[activeQuizId];
          delete state.detailedResults[activeQuizId];
          state.activeQuiz.currentQuestionIndex = 0;
        }

        if (!activeQuizId) {
          state.activeQuiz.launchedAt = null;
          state.activeQuiz.currentQuestionIndex = 0;
        }

        state.activeQuizId = activeQuizId;
        state.activeQuizSessionId = activeQuizSessionId;
        state.ids = ids;
        state.entities = entities;
      })
      .addCase(signalingQuizLaunched, (state, action) => {
        const { data: payload } = action.payload;

        if (isQuiz(action.payload.data)) {
          const launchedQuiz = action.payload.data as LaunchedQuizPayload;

          const questions: QuizQuestion[] = [];

          // This check indicates network reconnection vs refresh/rejoin
          const hadLocalSelections = Object.keys(state.activeQuiz.localSelections).length > 0;

          for (const question of launchedQuiz.questions) {
            const { id, text, multipleChoices, choices, selectedChoices, secondsRemaining } =
              question;

            if (selectedChoices) {
              state.activeQuiz.localSelections[id] = selectedChoices;
            }

            questions.push({ id, text, multipleChoices, choices, secondsRemaining });
          }

          const sessionChanged =
            state.activeQuizSessionId && state.activeQuizSessionId !== launchedQuiz.sessionId;
          const isNetworkReconnection =
            state.activeQuizSessionId === launchedQuiz.sessionId && hadLocalSelections;

          state.activeQuizId = launchedQuiz.id;
          state.activeQuizSessionId = launchedQuiz.sessionId;
          state.entities[launchedQuiz.id] = {
            ...launchedQuiz,
            questions,
          };

          // Reorder so the active quiz is always on top
          state.ids = state.ids.filter((id) => id !== launchedQuiz.id);
          state.ids.unshift(launchedQuiz.id);

          // Reset for new quiz session
          if (sessionChanged) {
            delete state.activeQuiz.localSelections[launchedQuiz.id];
            delete state.results[launchedQuiz.id];
            delete state.detailedResults[launchedQuiz.id];
            state.activeQuiz.currentQuestionIndex = 0;
          } else if (!isNetworkReconnection) {
            // Set to last question on first receive (browser refresh, rejoin, or first join)
            // Skip on network reconnection to preserve user's navigation
            state.activeQuiz.currentQuestionIndex = Math.max(questions.length - 1, 0);
          }
        } else {
          const quiz = state.entities[payload.id];
          if (quiz) {
            quiz.status = 'launched';
            if ('totalSecondsRemaining' in payload) {
              quiz.totalSecondsRemaining = payload.totalSecondsRemaining;
            }
          }
        }
      })
      .addCase(submitQuizQuestion.fulfilled, (state, action) => {
        const { id, questionId, choices, nextQuestion } = action.payload;

        const quiz = state.entities[id];
        if (quiz) {
          const existingQuestionIndex = quiz.questions.findIndex(
            (question) => question.id === nextQuestion.id
          );

          state.activeQuiz.localSelections[questionId] = choices;

          if (existingQuestionIndex === -1) {
            quiz.questions.push(nextQuestion);
          } else if (nextQuestion.secondsRemaining !== undefined) {
            quiz.questions[existingQuestionIndex].secondsRemaining = nextQuestion.secondsRemaining;
          }
        }
      })
      .addCase(signalingQuizEdited, (state, action) => {
        state.entities[action.payload.id] = action.payload;
      })
      .addCase(signalingQuizEnded, (state, action) => {
        const { id } = action.payload.data;
        const quiz = state.entities[id];
        if (quiz) {
          quiz.status = 'ended';
          quiz.editable = false;
        }

        if (state.activeQuizId === id) {
          state.activeQuizId = null;
          state.activeQuizSessionId = null;
          resetActiveQuiz(state);
        }
      })
      .addCase(signalingQuizDeleted, (state, action) => {
        const { id } = action.payload;
        delete state.entities[id];
        delete state.results[id];
        delete state.detailedResults[id];
        state.ids = state.ids.filter((qid) => qid !== id);
      })
      .addCase(signalingQuizFinished, (state, action) => {
        const { id } = action.payload;

        const quiz = state.entities[id];
        if (quiz) {
          quiz.submitted = true;
          resetActiveQuiz(state);
        }
      })
      .addCase(requestQuizResults.pending, (state, action) => {
        state.results[action.meta.arg] = {
          status: 'loading',
          data: null,
        };
      })
      .addCase(requestQuizResults.rejected, (state, action) => {
        state.results[action.meta.arg] = {
          status: 'error',
          data: null,
        };
      })
      .addCase(requestQuizResults.fulfilled, (state, action) => {
        state.results[action.meta.arg] = {
          status: 'success',
          data: action.payload,
        };
      })
      .addCase(requestDetailedQuizResults.pending, (state, action) => {
        const { quizId, participantId } = action.meta.arg;

        if (!state.detailedResults[quizId]) {
          state.detailedResults[quizId] = {};
        }

        state.detailedResults[quizId][participantId] = {
          status: 'loading',
          data: null,
        };
      })
      .addCase(requestDetailedQuizResults.rejected, (state, action) => {
        const { quizId, participantId } = action.meta.arg;

        if (!state.detailedResults[quizId]) {
          state.detailedResults[quizId] = {};
        }

        state.detailedResults[quizId][participantId] = {
          status: 'error',
          data: null,
        };
      })
      .addCase(requestDetailedQuizResults.fulfilled, (state, action) => {
        const { quizId, participantId } = action.meta.arg;

        if (!state.detailedResults[quizId]) {
          state.detailedResults[quizId] = {};
        }

        state.detailedResults[quizId][participantId] = {
          status: 'success',
          data: action.payload,
        };
      })
      .addCase(signalingQuizTemplatesReceived, (state, action) => {
        const { templates } = action.payload;

        state.templates.entities = {};
        state.templates.ids = [];

        for (const template of templates) {
          state.templates.entities[template.id] = template;
          state.templates.ids.push(template.id);
        }
      })
      .addCase(signalingQuizTemplateCreated, (state, action) => {
        const template = action.payload;

        state.templates.entities[template.id] = template;
        state.templates.ids.unshift(template.id);
      })
      .addCase(signalingQuizTemplateEdited, (state, action) => {
        const template = action.payload;

        state.templates.entities[template.id] = template;
      })
      .addCase(signalingQuizTemplateDeleted, (state, action) => {
        const { id } = action.payload;

        delete state.templates.entities[id];
        state.templates.ids = state.templates.ids.filter((tid) => tid !== id);
      });
  },
});

export const {
  exportQuizResultsRejected,
  exportQuizResultsRequested,
  exportQuizResultsFulfilled,
  quizResultsCleared,
  localSelectionSaved,
  quizLaunchedAtSet,
  quizQuestionNavigatedNext,
  quizQuestionNavigatedPrevious,
  quizQuestionIndexSet,
} = quizzesSlice.actions;

export const selectQuizById = (state: RootState, id: string) => state.quizzes.entities[id];

export const selectQuizEntities = (state: RootState) => state.quizzes.entities;

export const selectQuizLocalSelections = (state: RootState) =>
  state.quizzes.activeQuiz.localSelections;

export const selectQuizResults = (state: RootState, quizId: string) =>
  state.quizzes.results[quizId];

export const selectIndividualQuizResults = (
  state: RootState,
  quizId: string,
  participantId: string
) => state.quizzes.detailedResults[quizId]?.[participantId];

export const selectActiveQuizId = (state: RootState) => state.quizzes.activeQuizId;

export const selectActiveQuizLaunchedAt = (state: RootState) => state.quizzes.activeQuiz.launchedAt;

export const selectCurrentQuestionIndex = (state: RootState) =>
  state.quizzes.activeQuiz.currentQuestionIndex;

export const selectQuizTemplates = (state: RootState) => state.quizzes.templates.entities;

export const selectQuizTemplateIds = (state: RootState) => state.quizzes.templates.ids;

export const selectQuizTemplateById = (state: RootState, id: string) =>
  state.quizzes.templates.entities[id];

export const selectAllQuizTemplates = createSelector(
  [selectQuizTemplates, selectQuizTemplateIds],
  (entities, ids) => ids.map((id) => entities[id])
);

export const selectHasQuizTemplates = (state: RootState) => state.quizzes.templates.ids.length > 0;

export default quizzesSlice.reducer;
